<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import axios from 'axios';
import { ArrowPathIcon, BanknotesIcon } from '@heroicons/vue/24/outline';
import { CreditCardIcon, DevicePhoneMobileIcon, UserCircleIcon } from '@heroicons/vue/20/solid';
import EntityId from '@/components/ui/EntityId.vue';
import TimestampWithIso from '@/components/ui/TimestampWithIso.vue';
import { requestorLabel } from '@/components/card-tokens/cardTokenDisplay';
import { formatPercent } from '@/common/formatPercent';
import { getMessage } from '@/common/getMessage';
import { MonetaryAmount } from '@/common/monetary/MonetaryAmount';
import { useImmersveApiClient } from '@/hooks/useImmersveApiClient';
import type {
  AppliedFee,
  Payment,
  PaymentMerchant,
  PaymentStatus,
} from '@/integrations/Immersve/payments/types';

const props = defineProps<{ paymentId: string }>();

interface TransactionLine {
  label: string;
  value?: string;
  testId: string;
}

const STATUS_TONE_CLASSES: Record<PaymentStatus, string> = {
  created: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20',
  holding: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  cleared: 'bg-green-50 text-green-700 ring-green-600/20',
  canceled: 'bg-pink-50/60 text-pink-600 ring-pink-500/20',
};

const NOT_FOUND_OR_FORBIDDEN = "Payment not found, or you don't have access to it.";

const api = useImmersveApiClient();
const payment = ref<Payment>();
const loading = ref(true);
const errorMessage = ref<string>();

async function load() {
  loading.value = true;
  errorMessage.value = undefined;
  payment.value = undefined;
  try {
    payment.value = await api.getPayment(props.paymentId);
  } catch (err) {
    errorMessage.value = describeLoadError(err);
  } finally {
    loading.value = false;
  }
}

watch(() => props.paymentId, load, { immediate: true });

function describeLoadError(err: unknown): string {
  const cause = (err as { cause?: unknown } | undefined)?.cause;
  const status = axios.isAxiosError(cause) ? cause.response?.status : undefined;
  if (status === 403) {
    return NOT_FOUND_OR_FORBIDDEN;
  }
  return getMessage(err) ?? 'The payment could not be loaded.';
}

function sentenceCase(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  const words = value.replace(/-/g, ' ');
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
}

function yesNo(value: boolean | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value ? 'Yes' : 'No';
}

function cardLabel(current: Payment): string {
  const type = sentenceCase(current.card.type);
  return type ? `${type} card` : 'Card';
}

function merchantLocation(merchant: PaymentMerchant): string {
  return [merchant.city, merchant.countryCode].filter(Boolean).join(', ');
}

function feeCategory(fee: AppliedFee): string {
  return fee.category === 'fx' ? 'FX' : (sentenceCase(fee.category) ?? fee.category);
}

function cardPath(current: Payment): string {
  return `/cards/${encodeURIComponent(current.card.id)}`;
}

function accountPath(current: Payment): string {
  return `/cards?account=${encodeURIComponent(current.cardholder.id)}`;
}

function paymentPath(paymentId: string): string {
  return `/payments/${encodeURIComponent(paymentId)}`;
}

const transactionLines = computed<TransactionLine[]>(() => {
  const current = payment.value;
  if (!current) {
    return [];
  }
  const lines: TransactionLine[] = [
    { label: 'Type', value: sentenceCase(current.type), testId: 'payment-type' },
    {
      label: 'Credit / debit',
      value: sentenceCase(current.creditDebitIndicator),
      testId: 'payment-credit-debit',
    },
    { label: 'Pending', value: yesNo(current.pending), testId: 'payment-pending' },
    { label: 'Initiator', value: sentenceCase(current.initiator), testId: 'payment-initiator' },
    { label: 'Card present', value: yesNo(current.cardPresent), testId: 'payment-card-present' },
    {
      label: 'Cash operation',
      value: sentenceCase(current.cashOperation),
      testId: 'payment-cash-operation',
    },
    {
      label: 'Security challenge',
      value: sentenceCase(current.securityChallenge?.outcome),
      testId: 'payment-security-challenge',
    },
    {
      label: 'Challenge ref',
      value: current.securityChallenge?.ref,
      testId: 'payment-security-challenge-ref',
    },
    {
      label: 'Failure reason',
      value: sentenceCase(current.failureReason),
      testId: 'payment-failure-reason',
    },
  ];
  return lines.filter((line) => line.value !== undefined);
});
</script>

<template>
  <div>
    <div
      v-if="loading"
      data-test="payment-loading"
      class="flex items-center gap-2 py-6 text-sm text-gray-500"
    >
      <ArrowPathIcon class="h-4 w-4 animate-spin" />
      Loading…
    </div>
    <p v-else-if="errorMessage" data-test="payment-error" class="py-6 text-sm text-red-600">
      {{ errorMessage }}
    </p>
    <template v-else-if="payment">
      <div class="flex min-w-0 items-center gap-x-6 border-b border-gray-200 pb-8">
        <div class="flex size-16 flex-none items-center justify-center rounded-full bg-gray-100">
          <BanknotesIcon class="size-8 text-gray-500" aria-hidden="true" />
        </div>
        <div class="min-w-0">
          <div class="flex min-w-0 items-center gap-x-1.5 text-sm/6 text-gray-500">
            Payment
            <EntityId :value="payment.id" label="payment ID" data-test="payment-id" />
          </div>
          <h2 class="mt-1 text-base font-semibold text-gray-900" data-test="payment-title">
            {{ payment.merchant?.name ?? sentenceCase(payment.type) }}
          </h2>
        </div>
      </div>

      <div class="mt-8 grid grid-cols-1 items-start gap-x-8 gap-y-8 lg:grid-cols-3">
        <div class="lg:col-start-3 lg:row-end-1">
          <h2 class="sr-only">Summary</h2>
          <div class="rounded-lg bg-gray-50 outline-1 -outline-offset-1 outline-gray-900/5">
            <dl class="flex flex-wrap pb-6">
              <div class="flex-auto pt-6 pl-6">
                <dt class="text-sm/6 font-semibold text-gray-900">Amount</dt>
                <dd
                  class="mt-1 text-2xl font-semibold text-gray-900 tabular-nums"
                  data-test="payment-amount"
                >
                  {{ MonetaryAmount.fromPaymentAmount(payment.amount).format() }}
                </dd>
              </div>
              <div class="flex-none self-start px-6 pt-6">
                <dt class="sr-only">Status</dt>
                <dd
                  class="rounded-md px-2 py-1 text-xs font-medium capitalize ring-1 ring-inset"
                  :class="STATUS_TONE_CLASSES[payment.status]"
                  data-test="payment-status"
                >
                  {{ payment.status }}
                </dd>
              </div>
              <div class="mt-6 flex w-full flex-none gap-x-4 border-t border-gray-200 px-6 pt-6">
                <dt class="flex-none">
                  <span class="sr-only">Cardholder account</span>
                  <UserCircleIcon class="h-6 w-5 text-gray-400" aria-hidden="true" />
                </dt>
                <dd
                  class="flex min-w-0 flex-col items-start text-sm/6 text-gray-500"
                  data-test="payment-cardholder"
                >
                  Cardholder
                  <EntityId
                    :value="payment.cardholder.id"
                    label="account ID"
                    :to="accountPath(payment)"
                    data-test="payment-account-id"
                  />
                </dd>
              </div>
              <div class="mt-4 flex w-full flex-none gap-x-4 px-6">
                <dt class="flex-none">
                  <span class="sr-only">Card</span>
                  <CreditCardIcon class="h-6 w-5 text-gray-400" aria-hidden="true" />
                </dt>
                <dd class="flex min-w-0 flex-col items-start text-sm/6 text-gray-500">
                  {{ cardLabel(payment) }}
                  <EntityId
                    :value="payment.card.id"
                    label="card ID"
                    :to="cardPath(payment)"
                    data-test="payment-card-id"
                  />
                </dd>
              </div>
              <div v-if="payment.token" class="mt-4 flex w-full flex-none gap-x-4 px-6">
                <dt class="flex-none">
                  <span class="sr-only">Token</span>
                  <DevicePhoneMobileIcon class="h-6 w-5 text-gray-400" aria-hidden="true" />
                </dt>
                <dd class="text-sm/6 text-gray-500" data-test="payment-token-requestor">
                  Paid with {{ requestorLabel(payment.token) }}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <div
          class="-mx-4 px-4 py-8 shadow-xs ring-1 ring-gray-200 sm:mx-0 sm:rounded-lg sm:px-8 lg:col-span-2 lg:row-span-2 lg:row-end-2 xl:px-12"
        >
          <h2 class="text-base font-semibold text-gray-900">Details</h2>
          <dl class="mt-6 grid grid-cols-1 text-sm/6 sm:grid-cols-2">
            <div class="min-w-0 sm:pr-4">
              <dt class="text-gray-500">Created</dt>
              <dd class="mt-0.5" data-test="payment-created">
                <TimestampWithIso :value="payment.createdAt" />
              </dd>
            </div>
            <div class="mt-2 min-w-0 sm:mt-0 sm:pl-4">
              <dt class="text-gray-500">Modified</dt>
              <dd class="mt-0.5" data-test="payment-modified">
                <TimestampWithIso :value="payment.modifiedAt" />
              </dd>
            </div>
            <div class="mt-6 border-t border-gray-200 pt-6 sm:pr-4">
              <dt class="font-semibold text-gray-900">Merchant</dt>
              <dd class="mt-2 text-gray-500">
                <template v-if="payment.merchant">
                  <span
                    v-if="payment.merchant.name"
                    class="block font-medium text-gray-900"
                    data-test="payment-merchant"
                  >
                    {{ payment.merchant.name }}
                  </span>
                  <span v-if="merchantLocation(payment.merchant)" class="block">
                    {{ merchantLocation(payment.merchant) }}
                  </span>
                  <span v-if="payment.merchant.categoryCode" class="block">
                    MCC
                    <span class="font-mono" data-test="payment-mcc">{{
                      payment.merchant.categoryCode
                    }}</span>
                  </span>
                  <span
                    v-if="payment.merchant.terminalId"
                    class="flex min-w-0 items-center gap-x-1"
                  >
                    Terminal
                    <EntityId
                      :value="payment.merchant.terminalId"
                      label="terminal ID"
                      data-test="payment-terminal-id"
                    />
                  </span>
                  <span v-if="payment.merchant.id" class="flex min-w-0 items-center gap-x-1">
                    Merchant ID
                    <EntityId
                      :value="payment.merchant.id"
                      label="merchant ID"
                      data-test="payment-merchant-id"
                    />
                  </span>
                </template>
                <span v-else data-test="payment-no-merchant">—</span>
              </dd>
            </div>
            <div class="mt-8 sm:mt-6 sm:border-t sm:border-gray-200 sm:pt-6 sm:pl-4">
              <dt class="font-semibold text-gray-900">Transaction</dt>
              <dd class="mt-2 text-gray-500">
                <span v-for="line in transactionLines" :key="line.testId" class="block">
                  {{ line.label }}
                  <span class="text-gray-900" :data-test="line.testId">{{ line.value }}</span>
                </span>
              </dd>
            </div>
          </dl>
          <div class="mt-6 border-t border-gray-200 pt-6">
            <h3 class="text-sm/6 font-semibold text-gray-900">Amounts</h3>
            <dl class="mt-4 text-sm/6">
              <div class="flex justify-between gap-x-4 py-2">
                <dt class="text-gray-500">Merchant amount</dt>
                <dd class="text-gray-700 tabular-nums" data-test="payment-acquirer-amount">
                  {{ MonetaryAmount.fromPaymentAmount(payment.acquirerAmount).format() }}
                </dd>
              </div>
              <div
                v-for="(fee, index) in payment.feeComposition"
                :key="index"
                class="flex justify-between gap-x-4 py-2"
                data-test="payment-fee-row"
              >
                <dt class="text-gray-500">
                  {{ feeCategory(fee) }} fee
                  <span v-if="fee.rate !== undefined" class="text-gray-400"
                    >· {{ formatPercent(fee.rate) }}</span
                  >
                </dt>
                <dd class="text-gray-700 tabular-nums">
                  {{ MonetaryAmount.fromPaymentAmount(fee.amount).format() }}
                </dd>
              </div>
              <div
                v-if="payment.feeComposition.length === 0"
                class="flex justify-between gap-x-4 py-2"
                data-test="payment-no-fees"
              >
                <dt class="text-gray-500">Fees</dt>
                <dd class="text-gray-700">None</dd>
              </div>
              <div class="mt-2 flex justify-between gap-x-4 border-t border-gray-200 pt-4">
                <dt class="font-semibold text-gray-900">Amount</dt>
                <dd
                  class="font-semibold text-gray-900 tabular-nums"
                  data-test="payment-amounts-total"
                >
                  {{ MonetaryAmount.fromPaymentAmount(payment.amount).format() }}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <div class="lg:col-start-3">
          <h2 class="text-sm/6 font-semibold text-gray-900">References</h2>
          <dl class="mt-6 space-y-4 text-sm/6">
            <div>
              <dt class="text-gray-500">Network reference</dt>
              <dd class="flex min-w-0 text-gray-900" data-test="payment-network-reference">
                <EntityId
                  v-if="payment.networkReference"
                  :value="payment.networkReference"
                  label="network reference"
                />
                <span v-else>—</span>
              </dd>
            </div>
            <div>
              <dt class="text-gray-500">Retrieval reference</dt>
              <dd class="flex min-w-0 text-gray-900" data-test="payment-retrieval-reference">
                <EntityId
                  v-if="payment.retrievalReference"
                  :value="payment.retrievalReference"
                  label="retrieval reference"
                />
                <span v-else>—</span>
              </dd>
            </div>
            <div>
              <dt class="text-gray-500">Related payment</dt>
              <dd class="flex min-w-0 text-gray-900" data-test="payment-related-payment">
                <EntityId
                  v-if="payment.relatedPaymentId"
                  :value="payment.relatedPaymentId"
                  label="related payment ID"
                  :to="paymentPath(payment.relatedPaymentId)"
                />
                <span v-else>—</span>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </template>
  </div>
</template>
